import React from "react"
import MyTooltip from "@common-utils/MyTooltip"

interface IActiviteCardProps {
     color: string // couleur metronic : "primary" | "success" | "danger" | "warning" | "info" ...
     icon: string // classe font-awesome, ex "fa-umbrella-beach"
     title: React.ReactNode
     badge?: React.ReactNode // ex badge d'état (cliquable)
     periodeLabel: React.ReactNode // ex "12/05/2026 → 19/05/2026"
     periodeTooltip?: React.ReactElement
     dureeLabel?: string // ex "11 jours"
     createdAtLabel?: string // ex "18/04/2026"
     onViewJustificatif?: () => void // si présent -> pastille "Justificatif"
     actions?: React.ReactNode // ex boutons Valider / Rejeter
}

// Ligne unitaire (arrêt maladie / demande de congés) de l'onglet « Activité » de la fiche staff.
// Présentation en une seule ligne : icône colorée, titre + état, période / durée / création, puis
// justificatif et actions à droite. Se replie proprement sur les petits écrans.
const ActiviteCard = ({ color, icon, title, badge, periodeLabel, periodeTooltip, dureeLabel, createdAtLabel, onViewJustificatif, actions }: IActiviteCardProps) => {
     const periodeBadge = (
          <span className={`badge badge-light-${color} fs-7 d-inline-flex align-items-center ${periodeTooltip ? "cursor-default" : ""}`}>
               <i className={`fas fa-calendar-day text-${color} me-2`} />
               {periodeLabel}
          </span>
     )

     return (
          <div className={`d-flex align-items-center flex-wrap gap-4 px-5 py-4 rounded bg-light bg-hover-secondary border border-gray-300 border-dashed`}>
               {/* Icône */}
               <div className={"symbol symbol-45px flex-shrink-0"}>
                    <span className={`symbol-label bg-light-${color}`}>
                         <i className={`fas ${icon} fs-2 text-${color}`} />
                    </span>
               </div>

               {/* Contenu principal */}
               <div className={"flex-grow-1 min-w-250px"}>
                    <div className={"d-flex align-items-center gap-2 mb-2"}>
                         <span className={"fw-bold text-gray-800 fs-6"}>{title}</span>
                         {badge}
                    </div>
                    <div className={"d-flex flex-wrap align-items-center gap-2"}>
                         {periodeTooltip ? <MyTooltip title={periodeTooltip}>{periodeBadge}</MyTooltip> : periodeBadge}
                         {dureeLabel && (
                              <span className={"badge badge-light fs-7 d-inline-flex align-items-center text-gray-700"}>
                                   <i className={"fas fa-hourglass-half text-gray-600 me-2"} />
                                   {dureeLabel}
                              </span>
                         )}
                         {createdAtLabel && (
                              <span className={"text-muted fs-8 d-inline-flex align-items-center ms-1"}>
                                   <i className={"far fa-clock me-1"} /> Créé le {createdAtLabel}
                              </span>
                         )}
                    </div>
                    {/* Justificatif : sur une nouvelle ligne, sous la période */}
                    {onViewJustificatif && (
                         <div className={"mt-2"}>
                              <MyTooltip title={"Consulter le justificatif"}>
                                   <span className={"text-primary text-hover-primary fw-bold fs-8 d-inline-flex align-items-center cursor-pointer"} onClick={onViewJustificatif}>
                                        <i className={"fas fa-file-alt text-primary me-1"} /> Justificatif
                                   </span>
                              </MyTooltip>
                         </div>
                    )}
               </div>

               {/* Actions */}
               {actions && <div className={"d-flex align-items-center gap-2 flex-shrink-0 ms-auto"}>{actions}</div>}
          </div>
     )
}

export default ActiviteCard
